/**
 * TrainingLaunchFallbackDialog — the "Open training directly" escape hatch.
 *
 * Mounted once (app-level, in TrainingLaunchDisclosureProvider). It appears only
 * when a monitored launch could not open the provider tab (popup-blocked, or a
 * consent/session failure) and a target URL is known. It offers a PLAIN anchor
 * (target="_blank" rel="noopener noreferrer") — a fresh user gesture that Safari
 * honors even when programmatic window.open was blocked. Opening the training
 * here does NOT mark it complete; completion stays user-selected afterward.
 */
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTrainingLaunchFallbackStore } from '@/stores/trainingLaunchFallbackStore';

export function TrainingLaunchFallbackDialog() {
  const url = useTrainingLaunchFallbackStore((s) => s.url);
  const reason = useTrainingLaunchFallbackStore((s) => s.reason);
  const clear = useTrainingLaunchFallbackStore((s) => s.clear);

  return (
    <Dialog open={!!url} onOpenChange={(o) => { if (!o) clear(); }}>
      <DialogContent data-testid="training-launch-fallback-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Open the training</DialogTitle>
          <DialogDescription data-testid="training-launch-fallback-body">
            {reason === 'launch_failed'
              ? 'We could not finish setting up monitoring, but you can still open the training below. Opening it does not mark it complete.'
              : 'Your browser blocked the new tab. Use the link below to open the training directly. Opening it does not mark it complete.'}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => clear()}
              data-testid="training-launch-fallback-link"
              className="inline-flex w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Open training directly
            </a>
          )}
          <Button
            type="button"
            variant="ghost"
            onClick={clear}
            data-testid="training-launch-fallback-dismiss"
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
